import { defineProperty as _defineProperty, objectSpread as _objectSpread, extends as _extends, inherits as _inherits, classCallCheck as _classCallCheck, possibleConstructorReturn as _possibleConstructorReturn, getPrototypeOf as _getPrototypeOf, assertThisInitialized as _assertThisInitialized, slicedToArray as _slicedToArray, createClass as _createClass, objectWithoutProperties as _objectWithoutProperties } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { withContext, marginBottomLevels, eventBus, getMarginBottomClass, gridStyles } from '@piggybank/core';
import { Consumer } from '../Field/context.js';
import pick from 'lodash/fp/pick';
import omit from 'lodash/fp/omit';
import identity from 'lodash/fp/identity';
import entries from 'lodash/fp/entries';
import clamp from 'lodash/fp/clamp';
import last from 'lodash/fp/last';
import styles from './range-input.css.js';

var calculateProgressPercentage = function calculateProgressPercentage(value, min, max) {
  return (+value - min) / (max - min) * 100;
};

var propTypes = {
  /**
   * 0, 1, 2, 3, 4, 5, 6, 7
   */
  marginBottom: PropTypes.oneOf(marginBottomLevels),

  /** docgen-from-context:<Field/> */
  value: PropTypes.string.isRequired,
  min: PropTypes.string.isRequired,
  max: PropTypes.string.isRequired,
  step: PropTypes.string.isRequired,
  tickLocations: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  trackLabels: PropTypes.object,
  tooltipSlot: PropTypes.node,

  /**
   * docgen-from-context:<Field/>
   * ---
   * proxied to eventBus
   */
  onChange: PropTypes.func.isRequired,

  /** docgen-from-context:<Field/> */
  onBlur: PropTypes.func,
  fullWidth: PropTypes.bool,

  /** docgen-from-context:<Field/> */
  invalid: PropTypes.bool,

  /** docgen-from-context:<Field/> */
  describers: PropTypes.string,

  /** docgen-from-context:<Field/> */
  name: PropTypes.string.isRequired,
  scale: PropTypes.shape({
    convert: PropTypes.func.isRequired,
    invert: PropTypes.func.isRequired
  }),
  leftClamp: PropTypes.string,
  rightClamp: PropTypes.string
};
var defaultProps = {
  marginBottom: 0,
  fullWidth: false,
  scale: {
    convert: identity,
    invert: identity
  }
};
/** docgen-ignore */

var RangeInputInner = /*#__PURE__*/function (_Component) {
  _inherits(RangeInputInner, _Component);

  function RangeInputInner() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, RangeInputInner);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(RangeInputInner)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _defineProperty(_assertThisInitialized(_this), "state", {
      active: false
    });

    _defineProperty(_assertThisInitialized(_this), "renderTicks", function () {
      var _this$props = _this.props,
          tickLocations = _this$props.tickLocations,
          min = _this$props.min,
          max = _this$props.max,
          name = _this$props.name;
      return tickLocations ? React.createElement("div", {
        className: cn(styles.tickContainer),
        style: {
          width: "".concat(calculateProgressPercentage(last(tickLocations), min, max), "%")
        }
      }, tickLocations.map(function (tickLocation, index) {
        var progressPercentage = calculateProgressPercentage(tickLocation, min, max);
        var previousPercentage = index > 0 ? calculateProgressPercentage(tickLocations[index - 1], min, max) : 0;
        return React.createElement("div", {
          key: "".concat(name, "-tick-").concat(index),
          className: cn(styles.tick, _defineProperty({}, styles.tickCenter, progressPercentage !== 0 && progressPercentage !== 100)),
          style: {
            flex: "".concat(progressPercentage - previousPercentage, " 0 0px")
          }
        });
      })) : null;
    });

    _defineProperty(_assertThisInitialized(_this), "renderTrackLabels", function () {
      var _this$props2 = _this.props,
          trackLabels = _this$props2.trackLabels,
          min = _this$props2.min,
          max = _this$props2.max,
          name = _this$props2.name;
      return trackLabels ? React.createElement("div", {
        className: styles.trackLabelContainer,
        style: {
          width: "".concat(calculateProgressPercentage(last(Object.keys(trackLabels)), min, max), "%")
        }
      }, entries(trackLabels).map(function (_ref, index) {
        var _ref2 = _slicedToArray(_ref, 2),
            trackLabelPosition = _ref2[0],
            trackLabelText = _ref2[1];

        var progressPercentage = calculateProgressPercentage(trackLabelPosition, min, max);
        var previousPercentage = index > 0 ? calculateProgressPercentage(Object.keys(trackLabels)[index - 1], min, max) : 0;
        var trackLabelTextStyle = null;

        if (trackLabelPosition === min) {
          trackLabelTextStyle = {
            transform: 'translateX(100%)'
          };
        } else if (trackLabelPosition === max) {
          trackLabelTextStyle = {
            transform: 'none'
          };
        }

        return React.createElement("span", {
          key: "".concat(name, "-track-label-").concat(index),
          className: styles.trackLabel,
          style: {
            flex: "".concat(progressPercentage - previousPercentage, " 0 0px")
          }
        }, React.createElement("span", {
          className: styles.trackLabelText,
          style: trackLabelTextStyle
        }, trackLabelText));
      })) : null;
    });

    _defineProperty(_assertThisInitialized(_this), "handleChange", function (event) {
      var _this$props3 = _this.props,
          name = _this$props3.name,
          onChange = _this$props3.onChange,
          scale = _this$props3.scale;
      var value = scale.convert(event.target.value);
      eventBus.dispatch({
        type: 'onChange',
        component: 'RangeInput',
        value: value,
        name: name
      });
      onChange({
        value: value,
        event: event
      });
    });

    _defineProperty(_assertThisInitialized(_this), "handleMouseDown", function () {
      _this.setState({
        active: true
      });
    });

    _defineProperty(_assertThisInitialized(_this), "handleMouseUp", function () {
      _this.setState({
        active: false
      });
    });

    return _this;
  }

  _createClass(RangeInputInner, [{
    key: "render",
    value: function render() {
      var _cn2;

      var _omit = omit(['onChange', 'trackLabels'], this.props),
          name = _omit.name,
          marginBottom = _omit.marginBottom,
          value = _omit.value,
          min = _omit.min,
          max = _omit.max,
          step = _omit.step,
          fullWidth = _omit.fullWidth,
          invalid = _omit.invalid,
          describers = _omit.describers,
          tooltipSlot = _omit.tooltipSlot,
          tickLocations = _omit.tickLocations,
          forwardedRef = _omit.forwardedRef,
          scale = _omit.scale,
          leftClamp = _omit.leftClamp,
          rightClamp = _omit.rightClamp,
          rest = _objectWithoutProperties(_omit, ["name", "marginBottom", "value", "min", "max", "step", "fullWidth", "invalid", "describers", "tooltipSlot", "tickLocations", "forwardedRef", "scale", "leftClamp", "rightClamp"]); // Should only attempt to render a value within the range but a text input could
      // set field value outside the range.


      var scaledValue = scale.invert(value);
      var scaledMin = scale.invert(min);
      var scaledMax = scale.invert(max);
      var scaledLeftClamp = leftClamp ? scale.invert(leftClamp) : undefined;
      var scaledRightClamp = rightClamp ? scale.invert(rightClamp) : undefined;
      var scaledStep = (scaledMax - scaledMin) / ((max - min) / step);
      var valueInRange = clamp(leftClamp ? scaledLeftClamp : min, rightClamp ? scaledRightClamp : max, parseFloat(scaledValue));
      var valueProgress = calculateProgressPercentage(valueInRange, min, max);
      var leftClampProgress = leftClamp ? calculateProgressPercentage(scaledLeftClamp, min, max) : 0;
      var rightClampProgress = rightClamp ? 100 - calculateProgressPercentage(scaledRightClamp, min, max) : 0;
      return React.createElement("div", {
        className: cn(styles.RangeInput, getMarginBottomClass(marginBottom), (_cn2 = {}, _defineProperty(_cn2, styles.invalid, invalid), _defineProperty(_cn2, styles.fullWidth, fullWidth), _defineProperty(_cn2, gridStyles['size-12'], !fullWidth), _defineProperty(_cn2, gridStyles['sizeMedium-7'], !fullWidth), _defineProperty(_cn2, gridStyles['sizeLarge-4'], !fullWidth), _cn2))
      }, tooltipSlot && React.cloneElement(tooltipSlot, {
        progressPercentage: calculateProgressPercentage(valueInRange, min, max)
      }), React.createElement("div", {
        className: cn(styles.inputContainer, _defineProperty({}, styles['inputContainer--hasTicks'], tickLocations))
      }, React.createElement("div", {
        className: styles.progressContainer
      }, React.createElement("div", {
        className: styles.trackProgress,
        style: {
          transform: "scaleX(".concat(valueProgress / 100, ")")
        }
      }), leftClamp && React.createElement("div", {
        className: styles.trackLeftClamp,
        style: {
          transform: "scaleX(".concat(leftClampProgress / 100, ")")
        }
      }), React.createElement("div", {
        className: styles.trackUnfilledProgress,
        style: {
          transform: "scaleX(".concat(1 - valueProgress / 100, ")")
        }
      }), rightClamp && React.createElement("div", {
        className: styles.trackRightClamp,
        style: {
          transform: "scaleX(".concat(rightClampProgress / 100, ")")
        }
      })), this.renderTicks(), React.createElement("div", {
        className: styles.rangeInputContainer
      }, React.createElement("input", _extends({
        "aria-invalid": invalid,
        "aria-describedby": describers,
        "aria-valuenow": value,
        "aria-valuetext": value,
        "aria-valuemax": max,
        "aria-valuemin": min,
        ref: forwardedRef,
        type: "range",
        name: name,
        min: leftClamp ? scaledLeftClamp : scaledMin,
        max: rightClamp ? scaledRightClamp : scaledMax,
        step: scaledStep,
        value: valueInRange,
        onChange: this.handleChange,
        className: styles.rangeInput,
        onMouseDown: this.handleMouseDown,
        onMouseUp: this.handleMouseUp,
        onMouseLeave: this.handleMouseUp,
        onTouchStart: this.handleMouseDown,
        onTouchEnd: this.handleMouseUp,
        onTouchCancel: this.handleMouseUp,
        style: {
          width: "".concat(100 - (leftClampProgress + rightClampProgress), "%"),
          left: "".concat(leftClampProgress, "%"),
          right: "".concat(rightClampProgress, "%")
        }
      }, rest)), React.createElement("div", {
        className: styles.thumbContainer
      }, React.createElement("div", {
        className: cn(styles.thumb, _defineProperty({}, styles.thumbActive, this.state.active)),
        style: {
          left: "".concat(valueProgress, "%")
        }
      })))), this.renderTrackLabels());
    }
  }]);

  return RangeInputInner;
}(Component);

_defineProperty(RangeInputInner, "defaultProps", _objectSpread({}, defaultProps));

RangeInputInner.propTypes = process.env.NODE_ENV !== "production" ? _objectSpread({}, propTypes) : {};
var RangeInput = React.forwardRef(function (props, ref) {
  return React.createElement(RangeInputInner, _extends({}, props, {
    forwardedRef: ref
  }));
});

RangeInput.Tooltip = function (_ref3) {
  var children = _ref3.children,
      _ref3$type = _ref3.type,
      type = _ref3$type === void 0 ? 'default' : _ref3$type,
      progressPercentage = _ref3.progressPercentage;
  return React.createElement("div", {
    className: cn(_defineProperty({}, styles.tooltipPromo, type === 'promo'))
  }, React.createElement("div", {
    className: styles.tooltipContainer
  }, React.createElement("div", {
    className: styles.tooltipSpacerLeft,
    style: {
      flex: "".concat(progressPercentage / 100)
    }
  }), React.createElement("div", {
    className: cn(styles.tooltip)
  }, children), React.createElement("div", {
    className: styles.tooltipSpacerRight,
    style: {
      flex: "".concat(1 - progressPercentage / 100)
    }
  })), React.createElement("div", {
    className: styles.tooltipTriangleContainer
  }, React.createElement("div", {
    className: styles.tooltipTriangle,
    style: {
      left: "".concat(progressPercentage, "%")
    }
  })));
};

RangeInput.Tooltip.propTypes = {
  children: PropTypes.node,
  type: PropTypes.oneOf(['default', 'promo']),
  progressPercentage: PropTypes.number
};
RangeInput.displayName = 'RangeInput';
RangeInput.Tooltip.displayName = 'Tooltip';
RangeInput.propTypes = _objectSpread({}, propTypes);
RangeInput.defaultProps = defaultProps;
var RangeInput$1 = withContext(Consumer, pick(['name', 'value', 'onChange', 'onBlur', 'invalid', 'required', 'describers']))(RangeInput);
export default RangeInput$1;
export { RangeInput, RangeInputInner };